/**
 * Module-level cache utilities.
 *
 * Instances are created at module scope (outside components) so their data
 * survives React unmount/remount across route changes. Data is only re-fetched
 * after the TTL expires.
 */

export interface ModuleCache<T> {
  get(): T | null;
  set(data: T): void;
  clear(): void;
}

export interface KeyedModuleCache<T> {
  get(key: string): T | null;
  set(key: string, data: T): void;
  delete(key: string): void;
}

/**
 * Creates a single-value module-level cache with TTL.
 *
 * @example
 * const cache = createCache<Product[]>(10 * 60 * 1000);
 * // In useEffect:
 * const cached = cache.get();
 * if (cached) { setState(cached); return; }
 * const data = await fetch(...);
 * cache.set(data);
 */
export function createCache<T>(ttlMs: number): ModuleCache<T> {
  let entry: { data: T; ts: number } | null = null;
  return {
    get(): T | null {
      if (entry && Date.now() - entry.ts < ttlMs) return entry.data;
      return null;
    },
    set(data: T): void {
      entry = { data, ts: Date.now() };
    },
    clear(): void {
      entry = null;
    },
  };
}

/**
 * Creates a keyed module-level cache with TTL.
 * Useful for per-user or per-store data.
 *
 * @example
 * const cache = createKeyedCache<Product[]>(5 * 60 * 1000);
 * // In useEffect:
 * const cached = cache.get(store.id);
 * if (cached) { setState(cached); return; }
 * const data = await fetch(...);
 * cache.set(store.id, data);
 */
export function createKeyedCache<T>(ttlMs: number): KeyedModuleCache<T> {
  const map = new Map<string, { data: T; ts: number }>();
  return {
    get(key: string): T | null {
      const entry = map.get(key);
      if (entry && Date.now() - entry.ts < ttlMs) return entry.data;
      return null;
    },
    set(key: string, data: T): void {
      map.set(key, { data, ts: Date.now() });
    },
    delete(key: string): void {
      map.delete(key);
    },
  };
}
